import { INTENT_VERSION, type CustodyApprovalAction, type CustodyIntent } from "@redchain/contracts";
import { getBase58Decoder, getCompiledTransactionMessageDecoder } from "@solana/kit";
import { decodeWireTransaction } from "../tx";

const TOKEN_2022 = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb";
const TOKEN_CLASSIC = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA";
const MPL_CORE = "CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d";
const SYSTEM = "11111111111111111111111111111111";

const SESSION_IX = ["register_player", "claim_rcd", "deposit_rcd", "deposit_vault", "claim_item", "deposit_item", "pay_relay_fee", "set_refund_pref"] as const;

/** These move value but do not carry the amount the signer rebuilds. */
const UNAPPROVABLE = new Set(["accept_wager", "cancel_wager", "buy_levels", "buy_listing"]);

const VALUE_IX: Record<string, CustodyApprovalAction> = {
  list_mon: "marketplace_list",
  cancel_listing: "marketplace_cancel",
  buy_listing_checked: "marketplace_buy",
  buy_levels_checked: "buy_levels",
  create_wager: "wager",
  burn_mon: "burn",
  burn_mon_for_rcd: "burn",
};

const SUMMARY: Record<CustodyApprovalAction, string> = {
  marketplace_list: "List a Pokémon",
  marketplace_cancel: "Cancel a listing",
  marketplace_buy: "Buy a listing",
  shop_purchase: "Shop purchase",
  burn: "Burn a Pokémon",
  buy_levels: "Buy levels",
  wager: "Place a wager",
  transfer: "Transfer",
  withdraw: "Withdraw",
};

export interface ValueMove {
  summary: string;
  intent: CustodyIntent;
}

interface TokenMove {
  destination: string;
  mint: string;
  amount: bigint;
  sku?: string;
  quantity?: string;
}

let discriminators: Map<string, { session: boolean; name: string; action?: CustodyApprovalAction }> | null = null;

async function instructionNames(): Promise<Map<string, { session: boolean; name: string; action?: CustodyApprovalAction }>> {
  if (discriminators) return discriminators;
  const map = new Map<string, { session: boolean; name: string; action?: CustodyApprovalAction }>();
  for (const name of SESSION_IX) map.set(await anchorDisc(name), { session: true, name });
  for (const name of UNAPPROVABLE) map.set(await anchorDisc(name), { session: false, name });
  map.set(await anchorDisc("withdraw_vault"), { session: false, name: "withdraw_vault" });
  for (const [name, action] of Object.entries(VALUE_IX)) map.set(await anchorDisc(name), { session: false, name, action });
  discriminators = map;
  return map;
}

async function anchorDisc(name: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`global:${name}`));
  return [...new Uint8Array(hash).subarray(0, 8)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function hex8(data: Uint8Array): string {
  return [...data.subarray(0, 8)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function readU64(data: Uint8Array, offset: number): bigint | null {
  if (data.length < offset + 8) return null;
  const view = new DataView(data.buffer, data.byteOffset + offset, 8);
  return view.getBigUint64(0, true);
}

function accountAt(accounts: string[], indices: readonly number[], index: number): string {
  const slot = indices[index];
  const value = slot === undefined ? undefined : accounts[slot];
  if (!value) throw new Error("instruction is missing an account");
  return value;
}

function pubkeyAt(data: Uint8Array, offset: number): string {
  if (data.length < offset + 32) throw new Error("instruction data is truncated");
  return getBase58Decoder().decode(data.subarray(offset, offset + 32));
}

function base(gameAccount: string, connectedWallet: string) {
  return { version: INTENT_VERSION, gameAccount, connectedWallet } as const;
}

function readShopSuffix(data: Uint8Array): { sku: string; quantity: string } | null {
  if (data.length < 4) return null;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const skuLen = view.getUint32(0, true);
  if (skuLen === 0 || 4 + skuLen + 4 > data.length) return null;
  const sku = new TextDecoder().decode(data.subarray(4, 4 + skuLen));
  const qtyLen = view.getUint32(4 + skuLen, true);
  const start = 8 + skuLen;
  if (qtyLen === 0 || start + qtyLen > data.length) return null;
  const quantity = new TextDecoder().decode(data.subarray(start, start + qtyLen));
  if (!sku || !quantity) return null;
  return { sku, quantity };
}

function tokenTransfer(data: Uint8Array, indices: readonly number[], accounts: string[], gameAccount: string): (TokenMove & { owner: string }) | null {
  const tag = data[0];
  if (tag === 3 && indices.length >= 3) {
    const amount = readU64(data, 1);
    if (amount === null) return null;
    return { owner: accounts[indices[2]!] ?? "", destination: accounts[indices[1]!] ?? "", mint: "", amount };
  }
  if (tag === 12 && indices.length >= 4) {
    const amount = readU64(data, 1);
    if (amount === null) return null;
    const move: TokenMove & { owner: string } = {
      owner: accounts[indices[3]!] ?? "",
      destination: accounts[indices[2]!] ?? "",
      mint: accounts[indices[1]!] ?? "",
      amount,
    };
    if (move.owner === gameAccount && data.length > 10) {
      const extra = readShopSuffix(data.subarray(10));
      if (extra) {
        move.sku = extra.sku;
        move.quantity = extra.quantity;
      }
    }
    return move;
  }
  return null;
}

function namedIntent(name: string, data: Uint8Array, indices: readonly number[], accounts: string[], gameAccount: string, connectedWallet: string): CustodyIntent {
  const head = base(gameAccount, connectedWallet);
  if (name === "list_mon") {
    const price = readU64(data, 8);
    if (price === null) throw new Error("list price is missing");
    return { ...head, action: "marketplace_list", params: { asset: accountAt(accounts, indices, 4), price: price.toString() } };
  }
  if (name === "cancel_listing") {
    return { ...head, action: "marketplace_cancel", params: { listing: accountAt(accounts, indices, 3) } };
  }
  if (name === "buy_listing_checked") {
    const expectedPriceRcd = readU64(data, 8);
    if (expectedPriceRcd === null) throw new Error("buy price is missing");
    return {
      ...head,
      action: "marketplace_buy",
      params: {
        listing: accountAt(accounts, indices, 3),
        asset: accountAt(accounts, indices, 5),
        expectedPriceRcd: expectedPriceRcd.toString(),
      },
    };
  }
  if (name === "buy_levels_checked") {
    if (data.length < 17) throw new Error("buy levels price is missing");
    const maxRcd = readU64(data, 9);
    if (maxRcd === null) throw new Error("buy levels price is missing");
    return {
      ...head,
      action: "buy_levels",
      params: {
        mon: accountAt(accounts, indices, 4),
        targetLevel: String(data[8]!),
        maxRcd: maxRcd.toString(),
      },
    };
  }
  if (name === "create_wager") {
    const stake = readU64(data, 16);
    if (stake === null || data.length < 24 + 32) throw new Error("wager stake is missing");
    return { ...head, action: "wager", params: { stake: stake.toString(), opponent: pubkeyAt(data, 24) } };
  }
  if (name === "burn_mon" || name === "burn_mon_for_rcd") {
    return { ...head, action: "burn", params: { asset: accountAt(accounts, indices, name === "burn_mon" ? 3 : 7) } };
  }
  throw new Error(`unclassified instruction ${name}`);
}

function summarize(intent: CustodyIntent): string {
  if (intent.action === "transfer") return `${SUMMARY.transfer} ${intent.params.amount} to ${intent.params.destinationOwner}`;
  if (intent.action === "withdraw") return `${SUMMARY.withdraw} ${intent.params.amount} to ${intent.params.destination}`;
  if (intent.action === "marketplace_buy") return `${SUMMARY.marketplace_buy} for ${intent.params.expectedPriceRcd}`;
  if (intent.action === "buy_levels") return `${SUMMARY.buy_levels} to ${intent.params.targetLevel} for ${intent.params.maxRcd}`;
  if (intent.action === "marketplace_list") return `${SUMMARY.marketplace_list} for ${intent.params.price}`;
  return SUMMARY[intent.action];
}

/**
 * Value-moving transactions need an approval before POST /custody/sign.
 * The intent is the same shape custody-wallet rebuilds from the instruction.
 */
export async function describeValueMove(bytes: Uint8Array, gameAccount: string, connectedWallet: string): Promise<ValueMove | null> {
  let message;
  try {
    message = getCompiledTransactionMessageDecoder().decode(decodeWireTransaction(bytes).messageBytes);
  } catch {
    return null;
  }
  if (!("instructions" in message)) return null;
  const accounts = message.staticAccounts.map(String);
  const names = await instructionNames();
  let intent: CustodyIntent | null = null;
  const playerMoves: TokenMove[] = [];
  const stockMoves: TokenMove[] = [];
  const coreTransfers: { asset: string; newOwner: string }[] = [];

  for (const ix of message.instructions) {
    const program = accounts[ix.programAddressIndex] ?? "";
    const data = new Uint8Array(ix.data ?? []);
    const indices = ix.accountIndices ?? [];
    const named = names.get(hex8(data));
    if (named?.session) continue;
    if (named?.name && UNAPPROVABLE.has(named.name)) {
      throw new Error(`${named.name} moves value but does not carry the amount`);
    }
    if (named?.name === "withdraw_vault") {
      const lamports = readU64(data, 8);
      if (lamports === null) throw new Error("vault withdrawal amount is missing");
      if (lamports === 0n) continue;
      intent = one(intent, {
        ...base(gameAccount, connectedWallet),
        action: "transfer",
        params: { mintOrAsset: SYSTEM, amount: lamports.toString(), destinationOwner: accountAt(accounts, indices, 0) },
      });
      continue;
    }
    if (named?.action) {
      intent = one(intent, namedIntent(named.name, data, indices, accounts, gameAccount, connectedWallet));
      continue;
    }
    if (program === TOKEN_2022 || program === TOKEN_CLASSIC) {
      const parsed = tokenTransfer(data, indices, accounts, gameAccount);
      if (!parsed) continue;
      if (parsed.owner === gameAccount) playerMoves.push(parsed);
      else stockMoves.push(parsed);
      continue;
    }
    if (program === MPL_CORE && data[0] === 14 && data[1] === 0) {
      coreTransfers.push({ asset: accountAt(accounts, indices, 0), newOwner: accountAt(accounts, indices, 4) });
    }
  }

  if (!intent && playerMoves.length === 1 && playerMoves[0]?.sku && playerMoves[0]?.quantity && stockMoves.length === 1) {
    const pay = playerMoves[0];
    const stock = stockMoves[0]!;
    intent = {
      ...base(gameAccount, connectedWallet),
      action: "shop_purchase",
      params: {
        sku: pay.sku!,
        quantity: pay.quantity!,
        rcdPaid: pay.amount.toString(),
        itemMint: stock.mint,
        amount: stock.amount.toString(),
      },
    };
  } else if (!intent && playerMoves.length > 0) {
    for (const move of playerMoves) {
      if (!move.mint) throw new Error("transfer mint is missing");
      intent = one(intent, {
        ...base(gameAccount, connectedWallet),
        action: "transfer",
        params: { mintOrAsset: move.mint, amount: move.amount.toString(), destinationOwner: move.destination },
      });
    }
  }

  if (!intent && coreTransfers.length > 0) {
    for (const core of coreTransfers) {
      intent = one(intent, {
        ...base(gameAccount, connectedWallet),
        action: "transfer",
        params: { mintOrAsset: core.asset, amount: "1", destinationOwner: core.newOwner },
      });
    }
  }

  if (!intent) return null;
  return { summary: summarize(intent), intent };
}

function one(current: CustodyIntent | null, next: CustodyIntent): CustodyIntent {
  if (current && (current.action !== next.action || JSON.stringify(current.params) !== JSON.stringify(next.params))) {
    throw new Error("transaction mixes value-moving actions");
  }
  return current ?? next;
}
